import { useEffect, useMemo } from 'react';
import { resourceCountEnvironments } from '@gonfalon/dogfood-flags/dogfood-flags';
import { List as immutableList, type List } from '@gonfalon/immutable-shim/immutableShim';
import { getInfiniteEnvironmentsByProjectQuery } from '@gonfalon/rest-api/queries/environments';
import { useInfiniteQuery } from '@tanstack/react-query';

import type { Environment } from '#utils/environments/records/Environment';
import { createEnvironment } from '#utils/environments/records/Environment';

const MAX_ENVIRONMENTS_API_LIMIT = 1000;

/**
 * Every environment in a project, as Immutable `Environment` records for the legacy screens that
 * still consume them.
 *
 * Replaces reading the environment list off the project record that the legacy `fetchProject`
 * thunk wrote into redux. Drains the environments query page by page the same way
 * `useEnvironmentRequirements` does; with `resource-count-environments` at its default of 300 and
 * a 1000-item page, that is one request for nearly every project.
 *
 * Returns the environments that have arrived so far rather than an empty list until the drain
 * finishes, so a screen keeps rendering with what it has. Callers that need the whole set before
 * acting on it (for example, aggregating requirements across every environment before a save)
 * should gate that action on `isComplete`, not the render.
 */
export function useAllProjectEnvironments(projectKey: string): {
  environments: List<Environment>;
  isComplete: boolean;
  isError: boolean;
} {
  const limit = Math.min(resourceCountEnvironments(), MAX_ENVIRONMENTS_API_LIMIT);
  const { data, hasNextPage, isFetchingNextPage, isError, fetchNextPage } = useInfiniteQuery({
    ...getInfiniteEnvironmentsByProjectQuery({ projectKey, query: { limit, offset: 0 } }),
    // Page by offset and stop on a short page. The shared options follow `_links.next` instead,
    // which loops forever against a cursor that does not advance (a replayed response served for
    // every offset does exactly that), and React reports the resulting effect storm as
    // "Maximum update depth exceeded".
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      (lastPage.items?.length ?? 0) < limit ? undefined : { offset: lastPageParam.offset + limit },
  });

  useEffect(() => {
    // Without the isError guard a failed page leaves hasNextPage set and isFetchingNextPage false,
    // which would re-run this effect and retry the request in a loop.
    if (hasNextPage && !isFetchingNextPage && !isError) {
      void fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  const environments = useMemo(
    () => immutableList((data?.pages ?? []).flatMap((page) => page.items ?? []).map(createEnvironment)),
    [data],
  );

  return {
    environments,
    isComplete: data !== undefined && !hasNextPage,
    isError,
  };
}

/**
 * One environment out of {@link useAllProjectEnvironments}, by key. Replaces the deleted legacy
 * single-environment fetch: the list is already cached for the settings routes, so this costs no
 * extra request. `isPending` stays true only while the drain is still running and the key has not
 * shown up yet, so a caller can render as soon as the environment arrives and only fall back to
 * "not found" once the whole list is in (or a page failed).
 */
export function useProjectEnvironment(
  projectKey: string,
  environmentKey: string,
): { environment: Environment | undefined; isPending: boolean } {
  const { environments, isComplete, isError } = useAllProjectEnvironments(projectKey);
  const environment = environments.find((env) => env.key === environmentKey);
  return { environment, isPending: environment === undefined && !isComplete && !isError };
}
